import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { normalizeAvailability, utcMinToLocalParts } from './dateUtils';
import { matchScore, sharedLanguages } from './matching';
import { canSee } from './blocking';
import { sharedRanges, sharedBlocks } from './overlap';

// Can this profile be matched yet? Matching needs the exam, both subject lists
// and at least one free slot.
export const profileReady = p =>
  !!(p?.name && p?.studyCategory && p?.strengths?.length && p?.wantsToLearn?.length && normalizeAvailability(p).local.length);

const WEEK_MIN = 7 * 24 * 60;

// Join back-to-back slots into one range (Mon 01:00–11:00), wrapping Sun→Mon.
export const mergeSlots = (slots) => {
  const out = [];
  [...slots].sort((a, b) => a.startMin - b.startMin).forEach(s => {
    const last = out[out.length - 1];
    if (last && last.endMin === s.startMin) last.endMin = s.endMin;
    else out.push({ startMin: s.startMin, endMin: s.endMin });
  });
  if (out.length > 1 && out[out.length - 1].endMin % WEEK_MIN === out[0].startMin) {
    const first = out.shift();
    out[out.length - 1].endMin += first.endMin - first.startMin;
  }
  return out;
};

// "Mon 01:00–11:00", or "Mon 23:00 – Tue 03:00" when the range runs into another day.
export const rangeLabel = (r, zone) => {
  const a = utcMinToLocalParts(r.startMin, zone);
  const b = utcMinToLocalParts(r.endMin, zone);
  if (r.endMin - r.startMin < 24 * 60 && a.weekday === b.weekday) return `${a.dayShort} ${a.time}–${b.time}`;
  return `${a.dayShort} ${a.time} – ${b.dayShort} ${b.time}`;
};

// Everyone in my exam category I can see (canSee: demo and test accounts), scored
// against me, best first; blocked people are left out.
export async function loadMatches(uid, me, blocked = {}) {
  const snap = await getDocs(query(collection(db, 'users'), where('studyCategory', '==', me.studyCategory)));
  return snap.docs
    .filter(d => d.id !== uid)
    .map(d => ({ uid: d.id, ...d.data() }))
    .filter(u => canSee(me, u) && !(u.uid in blocked))
    .map(u => {
      // Shared free time as UTC ranges (brief 032); the score counts whole 2-hour blocks.
      const commonSlots = sharedRanges(me, u);
      const sharedSlots = sharedBlocks(commonSlots);
      const canTeach = (u.strengths || []).filter(s => (me.wantsToLearn || []).includes(s));
      const canLearn = (me.strengths || []).filter(s => (u.wantsToLearn || []).includes(s));
      const sharedLangs = sharedLanguages(me.languages, u.languages);
      return {
        ...u,
        score: matchScore({ teachLearnPairs: canTeach.length + canLearn.length, commonSlots: sharedSlots, sharedLangs }),
        canTeach,
        canLearn,
        commonSlots,
        sharedSlots,
        sharedLangs,
      };
    })
    .filter(m => m.score > 20)
    .sort((a, b) => b.score - a.score);
}

// People I've messaged or had a session with, in either direction. The video room
// only opens for them (getVideoRoom checks the same), so only they get a video button.
export async function loadContacts(uid) {
  const [convos, sent, received] = await Promise.all([
    getDocs(query(collection(db, 'conversations'), where('participants', 'array-contains', uid))),
    getDocs(query(collection(db, 'sessions'), where('fromUid', '==', uid))),
    getDocs(query(collection(db, 'sessions'), where('toUid', '==', uid))),
  ]);
  const out = new Set();
  convos.docs.forEach(d => (d.data().participants || []).forEach(p => out.add(p)));
  sent.docs.forEach(d => out.add(d.data().toUid));
  received.docs.forEach(d => out.add(d.data().fromUid));
  out.delete(uid);
  return out;
}
